/**
 * counterparty (#2678 c) — the pure half of the Gegenpartei picker.
 *
 * Turns a raw ``organization`` entity-list page into pickable options and
 * decides when a typed term is worth a server read. The view half
 * (``CounterpartyPicker.tsx``) owns the input, the debounce and the two
 * conditionally-mounted read children.
 *
 * **Identity-only, by design.** Nothing here matches, scores or resolves a
 * counterparty from bank data, and nothing consumes ``counterparty_iban``:
 *  - **``cash_flow.counterparty_iban`` is IBAN-SHAPED, not verified** (measured by
 *    #2678-b): of the real WF dump's 2.441 shape-valid values only 1.148 (47 %)
 *    pass mod-97, and the ~53 % remainder is a MIX of SEPA Gläubiger-IDs and
 *    short-by-one IBANs. **#2741 has since told those classes apart** — the field is
 *    mod-97-gated and the Gläubiger-ID lives on `counterparty_creditor_id`. The rule is
 *    unchanged: a guessed link is indistinguishable, once stored, from a farmer's own,
 *    and that alone rules out auto-resolution.
 *  - **#2736** notes prod tenant ``3286ee88`` still carries ~4.550 singleton
 *    counterparties minted by the pre-#2680 writer, cleaned only by a post-deploy
 *    backfill re-run. Any threshold tuned against today's pool would bake in that
 *    artefact.
 * The farmer picks; this module only lists what she typed for.
 */

/** One pickable organization. ``id`` is what reaches ``counterparty_id``. */
export interface CounterpartyOption {
  id: string;
  /** Human name (``name`` → ``name_short`` → the raw id). */
  label: string;
  /** Secondary line (``org_type``), omitted when the entity carries none. */
  sublabel?: string;
}

/**
 * Shortest term that earns a server read. ``useEntities``' ``q`` is a
 * case-insensitive substring scan over ``entity_id`` + ``attributes::text``, so a
 * single character matches most of the pool: a full page of noise for no signal.
 */
export const COUNTERPARTY_MIN_QUERY_LENGTH = 2;

/** The trimmed term to search for, or ``null`` when it is not worth a read. */
export function counterpartySearchTerm(raw: string | undefined): string | null {
  const term = (raw ?? '').trim();
  return term.length >= COUNTERPARTY_MIN_QUERY_LENGTH ? term : null;
}

/** Non-empty display string for a JSONB cell, or ``null``. Structured cells
 *  (list/dict) are dropped rather than stringified — ``[object Object]`` in a
 *  Gegenpartei row is worse than no row. */
function cell(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'object') return null;
  const s = String(value).trim();
  return s === '' ? null : s;
}

/**
 * Resolve a row's entity id: the response's ``primary_key`` column first, then
 * ``entity_id``, then ``id``. Same chain the FarmPage organization lookup uses —
 * UDB-backed org rows surface only ``entity_id``, so a primary-key-only read
 * would yield options that submit an empty link.
 */
function rowId(row: Record<string, unknown>, primaryKey?: string | null): string | null {
  return (
    (primaryKey ? cell(row[primaryKey]) : null) ?? cell(row.entity_id) ?? cell(row.id)
  );
}

/**
 * Project one ``organization`` entity-list page into pickable options.
 * Id-less rows are dropped (an option that cannot be linked must not be
 * offered) and duplicate ids collapse to their first occurrence, so React keys
 * stay unique over a pool that — see the module header — is known to contain
 * near-duplicates today.
 */
export function organizationOptions(
  rows: Record<string, unknown>[] | undefined,
  primaryKey?: string | null,
): CounterpartyOption[] {
  const out: CounterpartyOption[] = [];
  const seen = new Set<string>();
  for (const row of rows ?? []) {
    const id = rowId(row, primaryKey);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const sublabel = cell(row.org_type);
    out.push({
      id,
      label: cell(row.name) ?? cell(row.name_short) ?? id,
      ...(sublabel ? { sublabel } : {}),
    });
  }
  return out;
}
